<script lang="ts" setup>
import { VdButton } from "@voodoo.io/voodoo-ui";
import { PropType, computed, ref, watch } from "vue";

import { AbtestCohort, Layer, useAbtestCohortSelectInputQuery } from "@/graphql/generated-types";

interface CohortOption {
  id: string;
  name: string;
  selected: boolean;
  disabled: boolean;
}

interface AbtestOption {
  id: string;
  name: string;
  layerId: string;
  // Only what the query selects (the full generated Layer carries more fields than we fetch)
  layer?: Pick<Layer, "id" | "name"> | null;
  selected: boolean;
  disabled: boolean;
  cohorts: CohortOption[];
}

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  platform: {
    type: String,
    validator: (val: string) => ["ios", "android", ""].includes(val),
    required: false,
    default: null,
  },

  gameVersion: {
    type: String,
    required: false,
    default: null,
  },

  segmentIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  modelValue: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue"]);

const args = ref({
  gameId: props.gameId,
  versionId: props.versionId,
  // segmentIds: props.segmentIds,
  // platform: props.platform,
  // gameVersion: props.gameVersion,
});

const { result, loading } = useAbtestCohortSelectInputQuery(args);

const abtests = computed(() => {
  const abtestsFromRequest = result.value?.game?.version?.abtests ?? [];

  /**
   * Let's augment the abtests and cohorts with their "selected" and "disabled" states
   */
  const res = abtestsFromRequest.map((abtest) => {
    const isAbtestSelected = props.modelValue.some((id) => id === abtest.id);

    const formattedCohorts = abtest.cohorts.map((cohort) => {
      const isCohortSelected = props.modelValue.some((id) => id === cohort.id);
      if (isCohortSelected) {
        return { ...cohort, selected: isCohortSelected, disabled: false };
      }

      return { ...cohort, selected: false, disabled: false };
    });

    if (isAbtestSelected) {
      return { ...abtest, selected: isAbtestSelected, disabled: false, cohorts: formattedCohorts };
    }

    return { ...abtest, selected: false, disabled: false, cohorts: formattedCohorts };
  });

  /**
   * Compare all the selected A/B Tests against all the A/B from the same layer
   * and disable those ones (only one layer selected at a time)
   */
  res.forEach((ab) => {
    if (ab.selected) {
      res.forEach(({ id, layerId }, index) => {
        if (layerId === ab.layerId && id !== ab.id) {
          res[index].disabled = true;
        }
      });
    }
  });

  return res;
});

const selectedAbtestsAndCohorts = computed(() => {
  const res: Array<[AbtestOption, CohortOption]> = [];

  abtests.value.forEach((abtest) => {
    if (abtest.selected) {
      const selectedCohort = abtest.cohorts.find((cohort) => (cohort as CohortOption).selected);
      res.push([abtest as AbtestOption, selectedCohort as CohortOption]);
    }
  });

  return res;
});

watch(props, () => {
  args.value = {
    gameId: props.gameId,
    versionId: props.versionId,
    // segmentIds: props.segmentIds,
    // platform: props.platform,
    // gameVersion: props.gameVersion,
  };
});

const currentAbtest = ref();
const currentCohorts = ref();

const selectAbtest = (id: string) => {
  const ab = abtests.value.find((abtest) => abtest.id === id);

  if (ab?.disabled) {
    return;
  }

  currentAbtest.value = ab ?? null;
  currentCohorts.value = ab?.cohorts ?? [];
};

/**
 * Update the visual ref of the active values and filter the modelValue according to what
 * is left inside the new A/B tests list
 */
watch(abtests, (newAbtests) => {
  if (currentAbtest.value) {
    const ab = newAbtests.find((abtest) => abtest.id === currentAbtest.value.id);

    currentAbtest.value = ab ?? null;
    currentCohorts.value = ab?.cohorts ?? [];
  }

  if (newAbtests.length) {
    const updatedModelValue = props.modelValue.filter(
      (value) =>
        newAbtests.map(({ id }) => id).includes(value) ||
        newAbtests.flatMap(({ cohorts }) => cohorts.map((c) => c.id)).includes(value)
    );
    emit("update:modelValue", updatedModelValue);
  } else {
    emit("update:modelValue", []);
  }
});

const deleteEntry = (abtestId: string, cohortId: string) => {
  const newValueWithRemovedPair = props.modelValue.filter(
    (selectedId) => selectedId !== abtestId && selectedId !== cohortId
  );
  emit("update:modelValue", newValueWithRemovedPair);
};

const selectOrRemoveCohort = (id: string) => {
  const currentCohort: CohortOption = currentAbtest.value.cohorts.find((cohort: AbtestCohort) => cohort.id === id);

  if (!currentCohort.selected) {
    emit("update:modelValue", [...props.modelValue, currentAbtest.value.id, currentCohort.id]);
  } else {
    deleteEntry(currentAbtest.value.id, currentCohort.id);
  }
};
</script>

<template>
  <div class="abtest-cohort-select-input-component">
    <div class="selector">
      <section class="header">A/B Tests / Layers</section>
      <section class="header">Cohorts</section>
      <section v-if="abtests.length" v-loading="loading" class="abtests-selection">
        <div
          v-for="abtest in abtests"
          :key="abtest.id"
          :class="['option', { selected: abtest.selected, disabled: abtest.disabled }]"
          @click="() => selectAbtest(abtest.id)"
        >
          <section>
            <div class="abtest-name">{{ abtest.name }}</div>
            <div class="layer-name">
              <VdIcon store="fa" name="layer-group" size="small" />
              {{ abtest.layer?.name }}
            </div>
          </section>
          <VdIcon store="fa" name="chevron-right" size="large" />
        </div>
      </section>
      <section v-if="!!currentAbtest" class="cohorts-selection">
        <div
          v-for="cohort in currentCohorts"
          :key="cohort.id"
          :class="['option', { selected: cohort.selected, disabled: !cohort.selected && currentAbtest.selected }]"
        >
          <div class="cohort-name">{{ cohort.name }}</div>
          <VdButton
            type="primary"
            plain
            :disabled="!cohort.selected && currentAbtest.selected"
            @click="() => selectOrRemoveCohort(cohort.id)"
          >
            {{ cohort.selected ? "Remove" : "Select" }}
          </VdButton>
        </div>
      </section>
      <section v-if="!abtests.length && !loading" class="no-abtests">
        <VdIcon store="fa" name="spell-check" size="small" />
        <span>There are no abtests with the criteria selected (try to change the above parameters)</span>
      </section>
    </div>
    <h5 v-if="selectedAbtestsAndCohorts.length">Selected Criterias</h5>
    <div v-if="selectedAbtestsAndCohorts.length" class="result">
      <section class="header">A/B Tests / Layers / Cohorts</section>
      <div v-for="([selectedAb, selectedCohort], index) in selectedAbtestsAndCohorts" :key="'plop' + index" class="row">
        <!-- layer / A/B test / Cohort -->
        <span>
          <VdIcon store="fa" name="layer-group" size="small" />
          {{ selectedAb.layer && selectedAb.layer.name }}&nbsp;/&nbsp;
          <VdIcon store="fa" name="spell-check" size="small" />
          {{ selectedAb.name }}&nbsp;/&nbsp;
          <VdIcon store="fa" name="vial" size="small" />
          {{ selectedCohort.name }}
        </span>
        <VdIcon store="fa" size="small" name="trash" @click="() => deleteEntry(selectedAb.id, selectedCohort.id)" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.abtest-cohort-select-input-component {
  width: 100%;
  .selector {
    min-height: 100px;
    overflow: hidden;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto minmax(200px, 1fr);

    > section:not(:nth-child(even)) {
      border-right: 1px solid $foggy-grey;
    }

    > section {
      max-height: 200px;
      overflow-y: scroll;

      &.no-abtests {
        grid-column: span 2;
        border: none;
        color: var(--vd-color-grey-light-3);
        display: grid;
        grid-template-columns: repeat(2, auto);
        grid-gap: size("XS");
        place-content: center;
        align-items: center;

        svg {
          color: var(--vd-color-grey-light-3);
        }
      }

      > .option {
        cursor: pointer;
        padding: 8px 12px;

        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        transition: all 0.1s ease-in-out;

        &:hover {
          background-color: var(--voodootune-option-hover-color);
        }

        &.selected {
          background-color: var(--vd-color-blue-light-8);
          color: var(--vd-color-blue-dark-1);
          svg {
            color: var(--vd-color-blue-dark-1);
          }

          &:hover {
            background-color: var(--voodootune-option-hover-color);
          }
        }

        &.disabled {
          cursor: not-allowed;
          background-color: var(--vd-color-grey-light-9);
          color: #909298;
          svg {
            color: #909298;
          }

          &:hover {
            background-color: var(--vd-color-grey-light-8);
          }
        }

        .abtest-name {
          line-height: 17px;
          margin-bottom: 4px;
        }
        .layer-name {
          font-size: font("S");
          line-height: 14px;
        }
      }
    }

    .header {
      border: 1px solid $foggy-grey;
      height: 22px;
      line-height: 22px;
      font-size: font("S");
      padding: 0 12px;

      &:nth-of-type(1) {
        border-right-color: transparent;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &:nth-of-type(2) {
        border-left-color: transparent;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }

  h5 {
    margin-top: size("M");
  }

  .result {
    .header {
      border: 1px solid $foggy-grey;
      height: 22px;
      line-height: 22px;
      font-size: font("S");
      padding: 0 12px;
      border-radius: 4px;
    }

    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 12px;

      &:nth-last-of-type(odd) {
        background-color: #fafafa;
      }

      :deep(.vd-icon) {
        cursor: pointer;
      }
    }
  }
}
</style>
